import { useParams } from 'react-router-dom';
import { Box, Title, Text, Stack, SegmentedControl, Group, Skeleton, Center, Loader } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { hashtagsService } from '../../services/hashtags';
import { ReviewCard } from '../../components/reviews/ReviewCard';

function ReviewCardSkeleton() {
  return (
    <Box style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
      <Group align="flex-start" gap="xs" wrap="nowrap">
        {/* Avatar */}
        <Skeleton circle h={32} w={32} style={{ flexShrink: 0 }} />

        <Stack gap={6} style={{ flex: 1 }}>
          {/* Username · date */}
          <Group gap={6}>
            <Skeleton h={12} w={90} radius="sm" />
            <Skeleton h={12} w={50} radius="sm" />
          </Group>

          {/* Star rating */}
          <Skeleton h={12} w={80} radius="sm" />

          {/* Product name */}
          <Skeleton h={12} w="45%" radius="sm" />

          {/* Review text — 3 lines */}
          <Skeleton h={11} radius="sm" />
          <Skeleton h={11} w="90%" radius="sm" />
          <Skeleton h={11} w="65%" radius="sm" />

          {/* Action row */}
          <Group gap={16} mt={4}>
            <Skeleton h={11} w={48} radius="sm" />
            <Skeleton h={11} w={36} radius="sm" />
          </Group>
        </Stack>
      </Group>
    </Box>
  );
}

export default function TagFeed() {
  const { slug } = useParams<{ slug: string }>();
  const [sort, setSort] = useState('newest');

  const { data: hashtag, isLoading: loadingTag } = useQuery({
    queryKey: ['hashtag', slug],
    queryFn: () => hashtagsService.getBySlug(slug!),
    enabled: !!slug,
  });

  const {
    data: feed,
    isLoading: loadingFeed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['hashtag-feed', slug, sort],
    queryFn: ({ pageParam }) => hashtagsService.getReviews(slug!, pageParam, 20, sort),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    enabled: !!slug,
  });

  const reviews = feed?.pages.flatMap((p) => p.items) ?? [];
  const sentinelRef = useInfiniteScroll(fetchNextPage, !!hasNextPage, isFetchingNextPage);

  // Track recently visited tags in localStorage
  useEffect(() => {
    if (!slug || !hashtag) return;
    try {
      const key = 'cartcritic_recent_tags';
      const stored = JSON.parse(localStorage.getItem(key) ?? '[]') as { slug: string; name: string }[];
      const filtered = stored.filter((t) => t.slug !== slug);
      filtered.unshift({ slug, name: hashtag.name });
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 8)));
    } catch {}
  }, [slug, hashtag]);

  if (!slug) return null;

  return (
    <Stack gap={0}>
      {/* Header */}
      <Box style={{ padding: '1rem 1rem 0.75rem', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Stack gap={4} mb="sm">
          {loadingTag ? (
            <>
              <Skeleton h={28} w={160} radius="sm" />
              <Skeleton h={13} w={80} radius="sm" />
            </>
          ) : (
            <>
              <Title order={2}>#{hashtag?.name ?? slug}</Title>
              <Text c="dimmed" size="sm">{hashtag?.reviewCount ?? 0} reviews</Text>
            </>
          )}
        </Stack>

        <SegmentedControl
          value={sort}
          onChange={setSort}
          data={[
            { value: 'newest', label: 'Newest' },
            { value: 'top', label: 'Most Helpful' },
            { value: 'rating', label: 'Top Rated' },
          ]}
          size="xs"
        />
      </Box>

      {/* Feed */}
      {loadingFeed ? (
        Array.from({ length: 6 }).map((_, i) => <ReviewCardSkeleton key={i} />)
      ) : reviews.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No reviews with #{slug} yet.</Text>
        </Center>
      ) : (
        <>
          {reviews.map(review => <ReviewCard key={review.id} review={review} showProduct />)}
          <div ref={sentinelRef} />
          {isFetchingNextPage && <Center py="sm"><Loader size="sm" /></Center>}
        </>
      )}
    </Stack>
  );
}
